import { ArrowRight } from "lucide-react";
import { IMAGES, IB_PROGRAMS, DOMAINS, STRANDS } from "../data/content";
import { useModals } from "../components/ModalContext";

function Overline({ children, light }) {
  return (
    <p className={`text-xs font-bold uppercase tracking-[0.25em] ${light ? "text-gold" : "text-gold-700"}`}>{children}</p>
  );
}

export default function LearningJourney() {
  const { openEnquiry } = useModals();

  return (
    <main data-testid="learning-journey-page">
      {/* Hero */}
      <section className="relative isolate overflow-hidden bg-navy" aria-label="Learning journey introduction">
        <img
          src={IMAGES.microscope}
          alt="Student engaged in laboratory research"
          className="absolute inset-0 -z-10 h-full w-full object-cover opacity-30"
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-navy via-navy/85 to-navy/50" aria-hidden="true" />
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-28 lg:px-8">
          <div className="max-w-3xl animate-fade-up">
            <Overline light>Learning Journey</Overline>
            <h1 className="mt-4 font-serif text-4xl font-bold leading-[1.1] tracking-tight text-white sm:text-5xl" data-testid="learning-journey-heading">
              The World Is Our Curriculum. <span className="text-gold">Tailored to Yours.</span>
            </h1>
            <p className="mt-6 text-base leading-relaxed text-slate-200 sm:text-lg">
              At The Experiential Education Foundation, a journey becomes educational when purpose, inquiry, reflection,
              and evidence are directly designed into it. We bridge standard classroom instruction with real-world field
              sites through two distinct academic architectures, ensuring every student, regardless of board or
              curriculum, builds portable, defensible learning evidence.
            </p>
          </div>
        </div>
      </section>

      {/* IB Architecture */}
      <section id="ib" className="scroll-mt-24 bg-ivory" aria-label="International Baccalaureate architecture">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <Overline>Academic Architecture I</Overline>
          <h2 className="mt-3 font-serif text-2xl font-semibold tracking-tight text-navy sm:text-3xl lg:text-4xl" data-testid="ib-heading">
            International Baccalaureate
          </h2>
          <p className="mt-4 max-w-3xl text-base leading-relaxed text-slate-600">
            Our dedicated IB architecture uses thematic Academic Collections that align seamlessly with IB principles,
            fostering international mindedness, student agency, and inquiry-based learning. We map every field experience
            to support your school’s unique Approaches to Learning (ATL) and core requirements.
          </p>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {IB_PROGRAMS.map((p, i) => (
              <button
                key={p.code}
                type="button"
                onClick={() => openEnquiry(`International Baccalaureate ${p.code}: ${p.title}`)}
                data-testid={`ib-program-${p.code.toLowerCase()}`}
                className="group relative rounded-lg border border-slate-200 bg-white p-7 text-left shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-gold hover:shadow-xl focus-visible:ring-2 focus-visible:ring-gold"
              >
                <span className="absolute right-6 top-6 font-serif text-4xl font-bold text-slate-100 transition-colors group-hover:text-gold-light" aria-hidden="true">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="inline-block rounded-sm bg-navy px-3 py-1 text-xs font-bold uppercase tracking-[0.2em] text-gold">
                  {p.code}
                </span>
                <h3 className="mt-5 text-xl font-semibold leading-snug text-navy">{p.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{p.text}</p>
                <span className="mt-6 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-gold-700">
                  Enquire <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* National & international boards framework */}
      <section id="framework" className="scroll-mt-24 bg-parchment" aria-label="Cambridge, CBSE, ICSE and ISC framework">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <Overline>Academic Architecture II</Overline>
          <h2 className="mt-3 font-serif text-2xl font-semibold tracking-tight text-navy sm:text-3xl lg:text-4xl" data-testid="framework-heading">
            Cambridge, CBSE, ICSE, &amp; ISC Framework
          </h2>
          <p className="mt-4 max-w-3xl text-base leading-relaxed text-slate-600">
            For national and international examination boards, we do not apply a static travel template. Instead, we use
            a structured Four-Pathway Architecture mapped across two developmental stages:{" "}
            <span className="font-semibold text-navy">Explore (Classes VIII to X)</span> and{" "}
            <span className="font-semibold text-navy">Apply (Classes XI to XII)</span>.
          </p>
          <div className="mt-10 grid gap-6 lg:grid-cols-2">
            {DOMAINS.map((d, i) => (
              <button
                key={d.name}
                type="button"
                onClick={() => openEnquiry(d.name)}
                data-testid={`domain-card-${i}`}
                className="group overflow-hidden rounded-lg border border-slate-200 bg-white text-left shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-gold hover:shadow-xl focus-visible:ring-2 focus-visible:ring-gold"
              >
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={d.image}
                    alt={`${d.name} field study`}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute left-4 top-4 rounded-sm bg-navy/90 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-gold">
                    Pathway {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-navy">{d.name}</h3>
                  <div className="mt-5 space-y-4">
                    <div className="border-l-2 border-gold pl-4">
                      <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-gold-700">Explore Stage · Classes VIII to X</p>
                      <p className="mt-1 text-sm leading-relaxed text-slate-600">{d.explore}</p>
                    </div>
                    <div className="border-l-2 border-navy pl-4">
                      <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-navy">Apply Stage · Classes XI to XII</p>
                      <p className="mt-1 text-sm leading-relaxed text-slate-600">{d.apply}</p>
                    </div>
                  </div>
                  <span className="mt-6 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-gold-700">
                    Enquire about this domain <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Five strands reminder */}
      <section className="bg-navy" aria-label="The five strands">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <Overline light>Foundation of every journey</Overline>
          <h2 className="mt-3 font-serif text-2xl font-semibold text-white sm:text-3xl" data-testid="lj-strands-heading">
            Curriculum · Action · Service · Immersion · Assessment
          </h2>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {STRANDS.map((s, i) => (
              <div key={s.title} className="rounded-lg border border-white/10 bg-white/5 p-5" data-testid={`lj-strand-${i}`}>
                <span className="font-serif text-3xl font-bold text-gold" aria-hidden="true">{s.letter}</span>
                <h3 className="mt-2 font-semibold text-white">{s.title}</h3>
                <p className="mt-1 text-xs leading-relaxed text-slate-300">{s.subtitle}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
